import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CampaignOutlined as PostIcon,
} from '@mui/icons-material';
import axios from 'axios';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const POSTING_GROUPS = [
  { value: 'marketplace', label: 'Marketplace (OLX style)' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'jobs', label: 'Jobs / Employees' },
  { value: 'rentals', label: 'Rentals / To-Let' },
  { value: 'services', label: 'Services (Electrician etc.)' },
  { value: 'real_estate', label: 'Real Estate' },
];

const emptyForm = {
  category_key: '',
  label: '',
  label_te: '',
  label_hi: '',
  description: '',
  posting_group: 'requirements',
  sort_order: 0,
  visible_on_home: true,
  visible_on_my_town: true,
  visible_to_citizens: true,
  visible_to_providers: false,
  is_enabled_for_citizens: true,
  requires_admin_approval: true,
  olx_style: false,
  job_alerts_style: false,
  default_show_phone: false,
  allow_phone_toggle: true,
  allow_citizen_visibility_toggle: true,
  target_provider_categories: '',
};

/**
 * App Admin: manage which citizen posting types are enabled (OLX / Job Alerts style),
 * where they appear (Home / My Town), and routing to providers vs citizens.
 */
const CitizenPostingCategories = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [snack, setSnack] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/citizen-postings/categories/admin`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const enabledCount = useMemo(
    () => rows.filter((r) => r.is_enabled_for_citizens && r.is_active).length,
    [rows],
  );

  const patchRow = async (id, patch) => {
    setSavingId(id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `${API_BASE_URL}/citizen-postings/categories/admin/${id}`,
        patch,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (res.data?.success) {
        setRows((prev) => prev.map((r) => (r.id === id ? res.data.data : r)));
      }
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Update failed',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveNew = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...form,
        target_provider_categories: form.target_provider_categories
          ? form.target_provider_categories.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const res = await axios.post(
        `${API_BASE_URL}/citizen-postings/categories/admin`,
        payload,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (res.data?.success) {
        setSnack({ severity: 'success', message: 'Category added' });
        setDialogOpen(false);
        setForm(emptyForm);
        loadRows();
      }
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Could not add category',
      });
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Citizen Posting Categories
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Control what citizens can post (To-Let, Jobs, Services…). Toggle OLX / Job Alerts style,
              Home & My Town visibility, and provider routing.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={`${enabledCount} enabled for citizens`} color="primary" variant="outlined" />
            <Button startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Add Category
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Category</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="center">Citizens can post</TableCell>
                  <TableCell align="center">OLX style</TableCell>
                  <TableCell align="center">Job Alerts style</TableCell>
                  <TableCell align="center">Home</TableCell>
                  <TableCell align="center">My Town</TableCell>
                  <TableCell align="center">→ Citizens</TableCell>
                  <TableCell align="center">→ Providers</TableCell>
                  <TableCell align="center">Approval</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{row.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.category_key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.posting_group} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.is_enabled_for_citizens}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { is_enabled_for_citizens: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.olx_style}
                        disabled={savingId === row.id}
                        onChange={(e) => patchRow(row.id, { olx_style: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.job_alerts_style}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { job_alerts_style: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.visible_on_home}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { visible_on_home: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.visible_on_my_town}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { visible_on_my_town: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.visible_to_citizens}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { visible_to_citizens: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.visible_to_providers}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { visible_to_providers: e.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!row.requires_admin_approval}
                        disabled={savingId === row.id}
                        onChange={(e) =>
                          patchRow(row.id, { requires_admin_approval: e.target.checked })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      No categories yet. Run migration or add one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box mt={3} p={2} bgcolor="grey.50" borderRadius={2}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <PostIcon color="primary" fontSize="small" />
            <Typography fontWeight={700}>Use case guide</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <strong>Bike repair</strong> — Citizens can post OFF; visible to providers ON; target
                bike mechanics only (edit provider keys in API/DB).
              </li>
              <li>
                <strong>Rental wanted</strong> — OLX style ON; visible to citizens ON; providers OFF.
              </li>
              <li>
                <strong>2nd hand bike</strong> — Both citizens & providers ON (showrooms + public feed).
              </li>
              <li>
                Disable <strong>Citizens can post</strong> to restrict a category entirely.
              </li>
            </ul>
          </Typography>
        </Box>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Citizen Posting Category</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Category key (unique slug)"
                value={form.category_key}
                onChange={(e) => setForm({ ...form, category_key: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Label (English)"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Posting group</InputLabel>
                <Select
                  value={form.posting_group}
                  label="Posting group"
                  onChange={(e) => setForm({ ...form, posting_group: e.target.value })}
                >
                  {POSTING_GROUPS.map((g) => (
                    <MenuItem key={g.value} value={g.value}>
                      {g.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Provider category keys (comma-separated)"
                value={form.target_provider_categories}
                onChange={(e) =>
                  setForm({ ...form, target_provider_categories: e.target.value })
                }
                helperText="From Shops & Services Index — e.g. vehicle_services__bike_mechanic"
                fullWidth
              />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.olx_style}
                      onChange={(e) => setForm({ ...form, olx_style: e.target.checked })}
                    />
                  }
                  label="OLX style listing"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.job_alerts_style}
                      onChange={(e) =>
                        setForm({ ...form, job_alerts_style: e.target.checked })
                      }
                    />
                  }
                  label="Job Alerts style"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.visible_to_providers}
                      onChange={(e) =>
                        setForm({ ...form, visible_to_providers: e.target.checked })
                      }
                    />
                  }
                  label="Route to service providers"
                />
              </FormGroup>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveNew}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!snack}
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          message={snack?.message}
        />
      </Container>
    </DashboardLayout>
  );
};

export default CitizenPostingCategories;
